// src/pages/Chat/ChatLayout.jsx
//
// Top-level chat page. Mount at route /chat in App.js.
// Wraps with AdminLayout or ManagerLayout based on user role.
//
// Add to App.js:
//   import ChatLayout from './pages/Chat/ChatLayout';
//   <Route path="/chat" element={<ChatLayout />} />

import React, { useMemo } from "react";
// Removed useChatSocket as it is now in PrivateRoute
import { useChatCrypto } from "../../hooks/useChatCrypto";
import useChatStore from "../../store/chatStore";
import ConversationList from "./ConversationList";
import MessagePanel from "./MessagePanel";

import useAuthStore from "../../store/authStore";

// ── Layout wrapper resolver ────────────────────────────────────────────────
// Dynamically import the correct layout based on role stored in auth store.
// Adjust the role check to match your system's role values.
function useLayoutComponent() {
  const role = useAuthStore((state) => state.role);
  return useMemo(() => {
    try {
      if (role === "MANAGER") {
        // eslint-disable-next-line
        const { default: ManagerLayout } = require("../../layouts/ManagerLayout");
        return ManagerLayout;
      }
      if (role === "EMPLOYEE") {
        // eslint-disable-next-line
        const { default: EmployeeLayout } = require("../../layouts/EmployeeLayout");
        return EmployeeLayout;
      }
    } catch {
      // fallback to admin layout
    }
    try {
      // eslint-disable-next-line
      const { default: AdminLayout } = require("../../layouts/AdminLayout");
      return AdminLayout;
    } catch {
      // If no layout exists, use a passthrough wrapper
      return ({ children }) => <>{children}</>;
    }
  }, [role]);
}

// ── Main component ─────────────────────────────────────────────────────────
export default function ChatLayout() {
  const Layout = useLayoutComponent();
 
  // Initialize E2EE keys
  useChatCrypto();
 
  // Read from the store — populated by useChatSocket on mount
  const currentUserId  = useChatStore((s) => s.currentUserId);
  const wsConnected    = useChatStore((s) => s.wsConnected);
  const isMaximized    = useChatStore((s) => s.isMaximized);
  const send           = useChatStore((s) => s.sendWebSocketMessage);
  const selectedConvId = useChatStore((s) => s.selectedConvId);
 
  return (
    <Layout>
      {/* Full-height chat shell */}
      <div
        className={`flex overflow-hidden transition-all duration-300 ${
          isMaximized 
            ? "fixed inset-0 z-[999] bg-slate-950" 
            : "rounded-xl border border-white/8"
        }`}
        style={!isMaximized ? { height: "calc(100vh - 90px)" } : { height: "100vh" }}
      >
        {/* ── Sidebar: Conversation List ── */}
        {!isMaximized && (
          <aside
            className={`
              w-full md:w-80 shrink-0 flex flex-col
              bg-slate-900/70 backdrop-blur-xl
              border-r border-white/8
              ${selectedConvId ? "hidden md:flex" : "flex"}
            `}
          >
            <ConversationList currentUserId={currentUserId} />
          </aside>
        )}
        {/* ── Main panel: Messages ── */}
        <main
          className={`
            flex-1 flex flex-col min-w-0
            bg-slate-950/60 backdrop-blur-xl
            ${selectedConvId ? "flex" : "hidden md:flex"}
          `}
        >
          <MessagePanelWithUser
            send={send}
            connected={wsConnected}
            currentUserId={currentUserId}
          />
        </main>
      </div>
    </Layout>
  );
}

// Separate sub-component so currentUserId flows into MessagePanel
// and the panel can properly determine isOwn for each bubble.
function MessagePanelWithUser({ send, connected, currentUserId }) {
  const selectedConvId = useChatStore((s) => s.selectedConvId);
  const rawMessages    = useChatStore((s) => s.messages[selectedConvId]);

  const enrichedMessages = React.useMemo(
    () => (rawMessages || []).map((msg) => ({
      ...msg,
      _isOwn: msg.sender?.employee_id === currentUserId,
    })),
    [rawMessages, currentUserId]
  );

  return (
    <MessagePanel
      send={send}
      connected={connected}
      currentUserId={currentUserId}
      enrichedMessages={enrichedMessages}
    />
  );
}